import {
  Accordion,
  Badge,
  Card,
  Code,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";

interface ManifestData {
  version: string;
  permissions: string[];
  webhookEvents?: string[];
  metafieldNamespaces?: string[];
  extensions?: { type: string; label: string; activePages?: string[] }[];
  frontendModules?: {
    name: string;
    displayName: string;
    description?: string | null;
    exportName?: string | null;
  }[];
}

interface ManifestSummaryProps {
  manifest: ManifestData;
  showActivePages?: boolean;
}

export function ManifestSummary({
  manifest,
  showActivePages = false,
}: ManifestSummaryProps) {
  const webhookEvents = manifest.webhookEvents ?? [];
  const metafieldNamespaces = manifest.metafieldNamespaces ?? [];
  const extensions = manifest.extensions ?? [];
  const frontendModules = manifest.frontendModules ?? [];

  return (
    <Stack gap="md">
      <Card withBorder>
        <Title order={6} mb="xs">
          Permissions ({manifest.permissions.length})
        </Title>
        {manifest.permissions.length > 0 ? (
          <Group gap={6}>
            {manifest.permissions.map((scope) => (
              <Badge key={scope} variant="outline" size="sm">
                {scope}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            No permissions requested
          </Text>
        )}
      </Card>

      <Card withBorder>
        <Title order={6} mb="xs">
          Webhook Events ({webhookEvents.length})
        </Title>
        {webhookEvents.length > 0 ? (
          <Group gap={6}>
            {webhookEvents.map((event) => (
              <Badge key={event} variant="outline" size="sm" color="violet">
                {event}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            No webhook events subscribed
          </Text>
        )}
      </Card>

      {metafieldNamespaces.length > 0 && (
        <Card withBorder>
          <Title order={6} mb="xs">
            Metafield Namespaces ({metafieldNamespaces.length})
          </Title>
          <Group gap={6}>
            {metafieldNamespaces.map((ns) => (
              <Badge key={ns} variant="outline" size="sm" color="cyan">
                {ns}
              </Badge>
            ))}
          </Group>
        </Card>
      )}

      {extensions.length > 0 && (
        <Card withBorder>
          <Title order={6} mb="xs">
            Extensions ({extensions.length})
          </Title>
          <Table fz="sm" striped>
            <thead>
              <tr>
                <th>Type</th>
                <th>Label</th>
                {showActivePages && <th>Active Pages</th>}
              </tr>
            </thead>
            <tbody>
              {extensions.map((ext) => (
                <tr key={`${ext.type}-${ext.label}`}>
                  <td>
                    <Code>{ext.type}</Code>
                  </td>
                  <td>{ext.label}</td>
                  {showActivePages && ext.activePages && (
                    <td>
                      <Stack gap={2}>
                        {ext.activePages.map((page) => (
                          <Code key={page}>{page}</Code>
                        ))}
                      </Stack>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {frontendModules.length > 0 && (
        <Card withBorder>
          <Title order={6} mb="xs">
            Frontend Modules ({frontendModules.length})
          </Title>
          <Stack gap="sm">
            {frontendModules.map((mod) => (
              <Card key={mod.name} withBorder padding="sm">
                <Group justify="space-between">
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>
                      {mod.displayName}
                    </Text>
                    {mod.description && (
                      <Text size="xs" c="dimmed">
                        {mod.description}
                      </Text>
                    )}
                  </Stack>
                  {mod.exportName && <Code>{mod.exportName}</Code>}
                </Group>
              </Card>
            ))}
          </Stack>
        </Card>
      )}

      <Accordion variant="separated">
        <Accordion.Item value="raw">
          <Accordion.Control>
            <Text size="sm" fw={500}>
              View Raw Manifest
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Code block>{JSON.stringify(manifest, null, 2)}</Code>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
